import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Col, Row, Button, Form, Container } from "react-bootstrap";
import {
    AddAccessRightsButton,
    CustomModal,
    FiltersButton,
    TableHeader,
    WithRoleAccess,
} from "@services/ui-components";
import VioletBadge from "@services/ui-components/src/VioletBadge/VioletBadge";
import EmployeesTable from "./EmployeesTable/EmployeesTable";
import CustomReactSelect from "@services/ui-components/src/select/CustomReactSelect";
import ToastComponent from "../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../hooks/useToast";
import { requirementsInfoType } from "../../interfaces/interfaces";
import RequirementsFileInfo from "../../services/importFiles/RequirementsFileInfo";
import i18next from "i18next";
import { useReactiveVar } from "@apollo/client";
import { ImportFiles } from "../ImportFiles/ImportFiles";
import { ExportBtn } from "../ExportBtn/ExportBtn";
import { EntityType } from "../../utils/enums";
import { useGetEmployeesPositionsQuery } from "@me-team/host/main/addEmployee/graphql/addEmployee.hooks";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import {
    useGerCurrentBranchInfoForEmployeesListQuery,
    useGetStatusQuery,
} from "@me-team/host/main/employeeList/graphql/employeeList.hooks";
import ErrorService from "../../services/ErrorService";
import { CompanyAccess, EmployeeProxy } from "@me-team/host/main/graphql/types";
import { ErrorModalComponent } from "@me-pos/error-modal";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import { Option } from "../Logbook/constants";
import { useGetEmployeesNotInBranchQuery } from "@me-team/host/main/setUpAccessRole/graphql/setUpAccessRole.hooks";
import ReactSelect from "@services/ui-components/src/ReactSelect/ReactSelect";
import { DeepPartial } from "@apollo/client/utilities";
import { useEmployeesListAccess } from "./hooks/useEmployeesListAccess";
import { branchesLength } from "@me-team/host/main/branches/state";

const EmployeesList: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const { position } = useParams();
    const location = useLocation();
    const newEmployeeId = location.state?.employeeId;
    const navigate = useNavigate();
    const runJoy = localStorage.getItem("run") === "true";
    const [run] = useState(runJoy);
    const step = localStorage.getItem("step") || "0";
    const [stepIndex, setStepIndex] = useState(+step);
    const [isShowAddEmployeeFromAnotherBranchModal, setIsShowAddEmployeeFromAnotherBranchModal] = useState(false);
    const [employeesWithoutAccessCurrentBranch, setEmployeesWithoutAccessCurrentBranch] = useState<
        DeepPartial<EmployeeProxy[]>
    >([]);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(null);
    const [isSettingsModalShown, setIsSettingsModalShown] = useState(false);
    const totalActiveBranches = useReactiveVar(branchesLength);
    const employeesListAccess = useEmployeesListAccess({ newEmployeeId });
    const { showToast, toastText, setShowToast } = useToast();
    const selectPositionRef = useRef<{ reset: () => void }>(null);
    const selectStatusRef = useRef<{ reset: () => void }>(null);
    const hasProcessedNewEmployeeSetupModalRef = useRef(false);

    const handleResetClick = (ref: React.RefObject<{ reset: () => void }>) => {
        if (ref.current) {
            ref.current.reset();
        }
    };
    const inputNameRef = useRef<HTMLInputElement>(null);
    const [showFilters, setShowFilters] = useState(false);
    const [filter, setFilter] = useState({ name: "", positionId: position || "", statusId: "", branchId: null });
    const [currentFilter, setCurrentFilter] = useState({ positionId: "", statusId: "", selectedBranch: null });
    const [currentPage, setCurrentPage] = useState(1);
    const { data: poss } = useGetEmployeesPositionsQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
        onError: (error) => {
            console.error(error);
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const { data: statuses } = useGetStatusQuery({
        skip: !currentCompanyId,
    });

    const { data: currentBranch, loading: currentBranchLoading } = useGerCurrentBranchInfoForEmployeesListQuery({
        skip: !currentCompanyId,
        variables: { companyId: Number(currentCompanyId), branchId: Number(branchId) },
        context: { errorType: "local" },
        onCompleted: (data) => {
            const currentBranch = data.user.company.branches[0].id;
            setFilter((prev) => ({
                ...prev,
                branchId: currentBranch,
            }));
        },
        onError: (error) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const branches = currentBranch?.user?.company?.branches;
    const firstBranchId = branches?.[0]?.id;
    const firstBranchName = branches?.[0]?.name;

    const currentBranchOption = useMemo<Option>(
        () => ({
            value: firstBranchId ?? null,
            label: firstBranchName ?? "",
        }),
        [firstBranchId, firstBranchName]
    );

    const branchOptions = [currentBranchOption, { value: -1, label: t("All branches") }];
    const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
    const [employeeRequirementsInfo, setEmployeeRequirementsInfo] = useState<requirementsInfoType>(null);
    const canOpenNewEmployeeServicesSetup = employeesListAccess.postCreate.canOpenEmployeeServicesSetup;

    useEffect(() => {
        if (
            !hasProcessedNewEmployeeSetupModalRef.current &&
            newEmployeeId &&
            canOpenNewEmployeeServicesSetup &&
            !employeesListAccess.isRoleAccessPending
        ) {
            hasProcessedNewEmployeeSetupModalRef.current = true;
            setIsSettingsModalShown(true);
        }
    }, [canOpenNewEmployeeServicesSetup, employeesListAccess.isRoleAccessPending, newEmployeeId]);

    const positions = poss?.user?.company?.employeePositions?.employeePositions;
    const status = statuses?.employeeStatus;
    const toggleFilters = () => setShowFilters(!showFilters);

    const {
        data: employeesNotInBranchData,
        loading: loadingEmployeesNotInBranch,
        refetch: refetchEmployeesNotInBranch,
    } = useGetEmployeesNotInBranchQuery({
        variables: {
            companyId: +currentCompanyId,
            excludeBranchId: +branchId,
        },
        context: { errorType: "local" },
        skip: !currentCompanyId || !branchId,
        onCompleted: (data) => {
            const employeesNotInBranch: DeepPartial<EmployeeProxy[]> =
                data?.user?.company?.employeesNotInBranch?.length && data?.user?.company?.employeesNotInBranch;
            employeesNotInBranch && setEmployeesWithoutAccessCurrentBranch(employeesNotInBranch);
        },
        onError: (error) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const employeesNotInBranch: DeepPartial<EmployeeProxy[]> =
        employeesNotInBranchData?.user?.company?.employeesNotInBranch;

    useEffect(() => {
        employeesNotInBranch && setEmployeesWithoutAccessCurrentBranch(employeesNotInBranch);
    }, [employeesNotInBranch]);

    useEffect(() => {
        if (currentBranch) {
            setCurrentFilter((prev) => ({ ...prev, selectedBranch: currentBranchOption }));
        }
    }, [currentBranch, currentBranchOption]);

    const handleShowAddEmployeeFromAnotherBranch = () => {
        setIsShowAddEmployeeFromAnotherBranchModal(true);
    };
    const handleCloseAddEmployeeFromAnotherBranch = () => {
        setIsShowAddEmployeeFromAnotherBranchModal(false);
    };

    useEffect(() => {
        if (currentCompanyId) {
            setEmployeeRequirementsInfo(
                RequirementsFileInfo.employeeFileRequirements(parseInt(currentCompanyId), t, i18next.language, branchId)
            );
        }
    }, [i18n.language, currentCompanyId, branchId, t]);

    const onOpenImportModalHandler = () => {
        setIsImportModalOpen(true);
    };
    const onCloseImportModalHandler = () => {
        setIsImportModalOpen(false);
    };

    const handlePositionChange = (name: string, id: string) => {
        setCurrentFilter((prevFilter) => ({
            ...prevFilter,
            positionId: id,
        }));
    };

    const handleStatusChange = (name: string, id: string) => {
        setCurrentFilter((prevFilter) => ({
            ...prevFilter,
            statusId: id,
        }));
    };
    const applyFilters = () => {
        setFilter({
            name: inputNameRef.current.value,
            positionId: currentFilter.positionId,
            statusId: currentFilter.statusId,
            branchId: currentFilter.selectedBranch.value,
        });
        setCurrentPage(1);
    };
    const clearFilters = () => {
        handleResetClick(selectPositionRef);
        handleResetClick(selectStatusRef);
        setCurrentFilter({ positionId: "", statusId: "", selectedBranch: currentBranchOption });
        setFilter({ name: "", positionId: "", statusId: "", branchId: currentBranchOption.value });
        if (inputNameRef.current) {
            inputNameRef.current.value = "";
        }
    };
    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            applyFilters();
        }
    };
    const goToNextStep = () => {
        const nextStepIndex = stepIndex + 1;
        setStepIndex(nextStepIndex);
        localStorage.setItem("step", nextStepIndex.toString());
    };
    const goToStep = (direction: string, newStep?: number) => {
        let newStepIndex = newStep ?? stepIndex;
        if (direction === "next") {
            newStepIndex += 1;
        } else if (direction === "back" && stepIndex > 0) {
            newStepIndex -= 1;
        }
        setStepIndex(newStepIndex);
        localStorage.setItem("step", newStepIndex.toString());
    };

    if (!currentCompanyId || !branchId || loadingEmployeesNotInBranch || currentBranchLoading) return <Spinner />;

    return (
        <>
            <VioletBadge>
                {t(
                    "On this page are all the employees of your company. To see more information, use the hints in the table header. To start work - configure the work schedule of employees "
                )}{" "}
                <i className="bi bi-calendar4-week"></i> {t("and choose for each service they perform")}{" "}
                <i className="bi bi-bookmark"></i> {t("Enable online recording if you need it")}{" "}
                <i className="bi bi-gear"></i> .
            </VioletBadge>

            <TableHeader
                title={t("List of employees")}
                paths={[
                    { path: location?.pathname, label: t("Personal") },
                    { path: location?.pathname, label: t("List of employees") },
                ]}
                buttons={
                    <div>
                        <Row className="gx-3 justify-content-end mt-3 mt-lg-0">
                            <WithRoleAccess permission={CompanyAccess.EmployeeExport}>
                                <Col xs={2}>
                                    <ExportBtn
                                        companyId={parseInt(currentCompanyId)}
                                        exportType={EntityType.EMPLOYEES}
                                        branchId={branchId}
                                        classes="d-none d-xxl-inline-block"
                                        exportByBranch={filter.branchId}
                                    />
                                </Col>
                            </WithRoleAccess>
                            <WithRoleAccess permission={CompanyAccess.EmployeeImport}>
                                <Col xs={2}>
                                    <button
                                        className="btn btn-outline-violet border-primary text-primary fw-normal d-flex align-items-center justify-content-center w-100"
                                        onClick={onOpenImportModalHandler}
                                    >
                                        <i className="bi bi-box-arrow-in-down"></i>
                                        <span className="d-none d-xxl-inline-block ms-1">{t("import")}</span>
                                    </button>
                                </Col>
                            </WithRoleAccess>
                            {totalActiveBranches > 1 && (
                                <WithRoleAccess permission={CompanyAccess.AccessManageBranchEmployees}>
                                    <Col xs={2} className="btn-width">
                                        <AddAccessRightsButton onShow={handleShowAddEmployeeFromAnotherBranch} />
                                    </Col>
                                </WithRoleAccess>
                            )}
                            <WithRoleAccess permission={CompanyAccess.EmployeeCreate}>
                                <Col xs={4}>
                                    <Link
                                        onClick={() => {
                                            navigate(`/employees-list/add-employee/${branchId}`);
                                            goToNextStep();
                                        }}
                                        className="btn btn-primary fw-bold w-100 text-truncate start-step my-first-step text-truncate"
                                    >
                                        <i className="bi bi-plus-lg me-2"></i>
                                        {t("Add Employee")}
                                    </Link>
                                </Col>
                            </WithRoleAccess>
                            <Col
                                className={`${
                                    employeesListAccess.header.hasHeaderActions ? "col-xxl-2" : "col-sm-5 col-xl-3"
                                }`}
                            >
                                <FiltersButton
                                    filter={filter}
                                    showFilters={showFilters}
                                    onClick={toggleFilters}
                                    classesText={`${
                                        employeesListAccess.header.hasHeaderActions
                                            ? "d-none d-xxl-inline-block"
                                            : "d-inline-block"
                                    }`}
                                />
                            </Col>
                        </Row>
                    </div>
                }
                showFilters={showFilters}
                colSize={employeesListAccess.header.hasHeaderActions ? 4 : 6}
                btnColSize={employeesListAccess.header.hasHeaderActions ? 8 : 6}
            >
                <Row>
                    <Col xs={12} xl={8} xxl={9}>
                        <Row className="gx-3">
                            <Col
                                xs={12}
                                sm={employeesListAccess.list.showBranchFilter ? 6 : 12}
                                lg={employeesListAccess.list.showBranchFilter ? 3 : 4}
                                className="pt-3 pt-xl-0"
                            >
                                <div className="position-relative">
                                    <Form>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            className="form-control pe-5 text-truncate"
                                            placeholder={t("Search by Name, Surname")}
                                            id="employeeList-search-field"
                                            ref={inputNameRef}
                                            onKeyDown={handleSearchKeyDown}
                                        />
                                    </Form>
                                    <div className="position-absolute top-50 end-0 translate-middle-y ">
                                        <i className="bi bi-search position-absolute top-50 end-0 translate-middle-y text-dark me-3"></i>
                                    </div>
                                </div>
                            </Col>
                            <Col
                                xs={12}
                                sm={6}
                                lg={employeesListAccess.list.showBranchFilter ? 3 : 4}
                                className="pt-3 pt-xl-0"
                            >
                                <CustomReactSelect
                                    ref={selectPositionRef}
                                    optionsArray={positions}
                                    placeholder={t("Select Position")}
                                    setField={handlePositionChange}
                                    fieldName={t("Select Position")}
                                    value="id"
                                    name={"employeeList-position-field"}
                                    id={"employeeList-position-field"}
                                />
                            </Col>
                            <Col
                                xs={12}
                                sm={6}
                                lg={employeesListAccess.list.showBranchFilter ? 3 : 4}
                                className="pt-3 pt-xl-0"
                            >
                                <CustomReactSelect
                                    ref={selectStatusRef}
                                    optionsArray={status}
                                    placeholder={t("Status")}
                                    setField={handleStatusChange}
                                    fieldName={t("Status")}
                                    value="id"
                                    name={"employeeList-status-field"}
                                    id={"employeeList-status-field"}
                                />
                            </Col>
                            {employeesListAccess.list.showBranchFilter ? (
                                <Col xs={12} sm={6} lg={3} className="pt-3 pt-xl-0">
                                    <ReactSelect
                                        id="employeeList-branchId-field"
                                        placeholder={t("Branch")}
                                        options={branchOptions}
                                        value={branchOptions.find(
                                            (option) => option.value === currentFilter?.selectedBranch?.value
                                        )}
                                        onChange={(selectedOption) =>
                                            setCurrentFilter((prevFilter) => ({
                                                ...prevFilter,
                                                selectedBranch: {
                                                    value: selectedOption.value,
                                                    label: selectedOption.label,
                                                },
                                            }))
                                        }
                                        isSearchable={false}
                                    />
                                </Col>
                            ) : null}
                        </Row>
                    </Col>

                    <Col xs={12} lg={6} xl={4} xxl={3} className="pt-3 pl-0 pt-xl-0 ms-lg-auto">
                        <Row className="gx-3 h-100">
                            <Col xs={6}>
                                <Button
                                    variant="outline-dark"
                                    className=" border-grey fw-normal btn-block w-100 h-100"
                                    onClick={clearFilters}
                                >
                                    {t("clear")}
                                </Button>
                            </Col>
                            <Col xs={6}>
                                <Button
                                    variant="primary"
                                    className="fw-normal btn-block w-100 btn-block h-100 "
                                    onClick={applyFilters}
                                >
                                    {t("Apply")}
                                </Button>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </TableHeader>
            <EmployeesTable
                filter={filter}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                isPremiumTariff={employeesListAccess.isPremiumTariff}
                employeesListAccess={employeesListAccess}
                position={position}
                goToNextStep={goToNextStep}
                goToStep={goToStep}
                run={run}
                stepIndex={stepIndex}
                setStepIndex={setStepIndex}
                isShowAddEmployeeFromAnotherBranchModal={isShowAddEmployeeFromAnotherBranchModal}
                onCloseAddEmployeeFromAnotherBranch={handleCloseAddEmployeeFromAnotherBranch}
                employeesWithoutAccessCurrentBranch={employeesWithoutAccessCurrentBranch}
                refetchEmployeesNotInBranch={refetchEmployeesNotInBranch}
            />
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
            {employeeRequirementsInfo && (
                <ImportFiles
                    currentRequirementsInfo={employeeRequirementsInfo}
                    isOpen={isImportModalOpen}
                    onClose={onCloseImportModalHandler}
                />
            )}
            {isErrorModalOpen && (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            )}
            <CustomModal
                show={isSettingsModalShown && canOpenNewEmployeeServicesSetup}
                handleClose={() => setIsSettingsModalShown(false)}
                title={
                    <Container>
                        <Row>
                            <h5 className="m-0 p-0 fw-normal">{t("Employee added!")}</h5>
                        </Row>
                    </Container>
                }
                body={
                    <Container>
                        <Row>
                            <Col>
                                <span className="fs-6 me-1">
                                    {t("Do you want to configure the services this employee provides?")}
                                </span>
                            </Col>
                        </Row>
                    </Container>
                }
                buttons={[
                    {
                        text: t("Later"),
                        onClick: () => setIsSettingsModalShown(false),
                        variant: "outline-primary",
                        className: "w-100 fw-normal",
                    },
                    ...(canOpenNewEmployeeServicesSetup
                        ? [
                              {
                                  text: t("Go to settings"),
                                  onClick: () => {
                                      navigate(`/employees-list/employee-services/${newEmployeeId}/${branchId}`);
                                  },
                                  variant: "primary",
                                  className: "w-100 fw-normal",
                              },
                          ]
                        : []),
                ]}
            />
        </>
    );
};

export default EmployeesList;
